import get from "lodash.get";
import {
  APPLICANT,
  APPLICATION,
  LISTING,
  PRODUCT_PAGES,
  REQUISITION,
  WORK_PERSONALITY_SURVEY,
  isProductPage,
  type AppDestination,
  type ProductPage,
} from "@compono/chat-island";

/**
 * Pages whose route is only registered for some users, so a link to one
 * resolves only when App.tsx would render it. Absent means closed: a link
 * that cannot open is plain words, never "page not found".
 */
export interface OpenPages {
  /** The requisitions route: Hire, the job requisitions flag, and permission to view them. */
  requisitions?: boolean;
  /** The product pages this user can open (`openProductPages`). */
  pages?: ReadonlySet<ProductPage>;
}

/** What decides whether a product page opens for someone. */
export interface PageAccess {
  /** Their permissions, read by path the way ShowIf reads them. */
  permissions: object;
  /** Hire reviewers and hiring managers have no talent pool. */
  talentRestricted: boolean;
  /** Interview guides and the question bank also depend on the Hire role. */
  canViewGuides: boolean;
  canViewBank: boolean;
  /** The LaunchDarkly gates some pages sit behind. */
  flags: {
    hireJobRequisitions: boolean;
    hireOfferManagement: boolean;
    interviewGuides: boolean;
    referenceChecks: boolean;
    cultureReport: boolean;
    assistantSettings: boolean;
  };
}

type Can = (permission: string) => boolean;

/**
 * Each product page's path, and when it opens: the condition its side-nav link
 * shows under, together with the one App.tsx registers its route under where
 * that is narrower. A link to a page someone could not open would be "page not
 * found", or a page of errors, so it resolves to nothing for them instead.
 */
const PRODUCT_PAGE_ROUTES: Record<
  ProductPage,
  { path: string; opens: (can: Can, access: PageAccess) => boolean }
> = {
  // People (PeopleNav), and the Users page's own tabs
  users: { path: "/users", opens: (can) => can("employers.view") },
  "pending-invitations": {
    path: "/users/pending",
    opens: (can) => can("employers.view"),
  },
  "deactivated-users": {
    path: "/users/deactivated",
    opens: (can) => can("employers.view"),
  },
  "role-permissions": {
    path: "/users/role-permissions",
    opens: (can) => can("employers.view"),
  },
  groups: {
    path: "/groups",
    opens: (can) => can("product.engageAdmin") || can("product.developAdmin"),
  },
  "organisation-structure": {
    path: "/settings",
    opens: (can) => can("organisations.edit"),
  },
  "job-levels": {
    path: "/settings/job-level",
    opens: (can) => can("organisations.edit"),
  },
  "job-titles": {
    path: "/settings/job-title",
    opens: (can) => can("organisations.edit"),
  },
  locations: {
    path: "/settings/location",
    opens: (can) => can("organisations.edit"),
  },
  // Settings (AdminNav)
  "account-settings": {
    path: "/account",
    opens: (can) => can("organisations.edit"),
  },
  "message-templates": {
    path: "/message-templates",
    opens: (can) => can("applicationMessages.createTemplates"),
  },
  "tag-library": {
    path: "/manage-tags",
    opens: (can) => can("isAbleToManageTags"),
  },
  "interview-guides": {
    path: "/interview-guides",
    opens: (can, { flags, canViewGuides }) =>
      can("product.hire") && flags.interviewGuides && canViewGuides,
  },
  "question-bank": {
    path: "/question-bank",
    opens: (can, { flags, canViewBank }) =>
      can("product.hire") && flags.interviewGuides && canViewBank,
  },
  "job-board-integration": {
    path: "/job-board-integration",
    opens: (can) => can("jobBoards.manageSubscription"),
  },
  "api-management": {
    path: "/employer-api",
    opens: (can) => can("organisations.manageApiKeys"),
  },
  sso: { path: "/sso", opens: (can) => can("organisations.manageSso") },
  "reference-check-questions": {
    path: "/guide-questions",
    opens: (can, { flags }) =>
      can("product.hire") &&
      flags.referenceChecks &&
      can("referenceChecks.manage"),
  },
  "approval-configuration": {
    path: "/approval-config",
    opens: (can, { flags }) =>
      can("product.hire") &&
      flags.hireJobRequisitions &&
      can("jobRequisitions.configure"),
  },
  "assistant-settings": {
    path: "/assistant-settings",
    opens: (_can, { flags }) => flags.assistantSettings,
  },
  "offer-document-templates": {
    path: "/offer-document-templates",
    opens: (can, { flags }) =>
      can("product.hire") &&
      flags.hireOfferManagement &&
      can("offers.configureTemplates"),
  },
  // Everyone's own
  "my-profile": { path: "/profile", opens: () => true },
  "calendar-connection": { path: "/calendar", opens: () => true },
  // Hire (HireNav, ReportsNav)
  jobs: { path: "", opens: (can) => can("product.hire") },
  "create-job": {
    path: "/listing/create",
    opens: (can) => can("product.hire") && can("listings.create"),
  },
  "talent-pool": {
    path: "/talent",
    opens: (can, { talentRestricted }) =>
      can("product.hire") && !talentRestricted,
  },
  messages: {
    path: "/messages",
    opens: (can) => can("product.hire") && can("applicationMessages.view"),
  },
  interviews: { path: "/interviews", opens: (can) => can("product.hire") },
  requisitions: {
    path: "/requisitions",
    opens: (can, { flags }) =>
      can("product.hire") &&
      flags.hireJobRequisitions &&
      can("jobRequisitions.view"),
  },
  "create-requisition": {
    path: "/requisitions/create",
    opens: (can, { flags }) =>
      can("product.hire") &&
      flags.hireJobRequisitions &&
      can("jobRequisitions.create"),
  },
  approvals: {
    path: "/approvals",
    opens: (can, { flags }) =>
      can("product.hire") &&
      flags.hireJobRequisitions &&
      can("jobRequisitions.viewApprovals"),
  },
  "hiring-teams": { path: "/teams", opens: (can) => can("teams.view") },
  "recruitment-reports": {
    path: "/reports",
    opens: (can) => can("hireReports.view"),
  },
  // Engage (EngageNav) and Develop (DevelopNav)
  surveys: { path: "/dashboard", opens: (can) => can("product.engage") },
  campaigns: {
    path: "/campaigns",
    opens: (can) => can("product.engage") && can("campaigns.view"),
  },
  culture: {
    path: "/culture",
    opens: (can) => can("product.engage") && can("insights.view"),
  },
  "work-personality": {
    path: "/workpersonality",
    opens: (can) =>
      can("product.engage") && can("insights.view") && can("personality.view"),
  },
  sharing: {
    path: "/sharing",
    opens: (can) => can("product.engage") && can("insights.share"),
  },
  timezone: { path: "/timezone", opens: (can) => can("product.engage") },
  "business-profile": {
    path: "/business-profile",
    opens: (can, { flags }) => can("product.engage") && flags.cultureReport,
  },
  courses: { path: "/courses", opens: (can) => can("product.develop") },
};

/** The product pages this user can open, for `OpenPages.pages`. */
export const openProductPages = (access: PageAccess): Set<ProductPage> => {
  const can: Can = (permission) => Boolean(get(access.permissions, permission));
  return new Set(
    PRODUCT_PAGES.filter((page: ProductPage) =>
      PRODUCT_PAGE_ROUTES[page].opens(can, access),
    ),
  );
};

/**
 * Where a named link in a Compono Assist answer goes, in this app.
 *
 * The assistant names places (`app-destination:work-personality-survey`) and
 * never writes a path, because the routes are this app's: when one moves,
 * change it here and nothing in the assistant. Whether a link is offered at
 * all is decided before it gets here (the survey only when Engage would open
 * it). A name not listed, or no organisation, resolves to nothing, and the
 * panel shows the words without a link.
 */
export const resolveAppLink = (
  orgId: string | undefined,
  { name, params }: AppDestination,
  open: OpenPages = {},
): string | undefined => {
  if (!orgId) return undefined;
  const org = `/organisation/${encodeURIComponent(orgId)}`;
  // A record's page needs its id; a link missing one goes nowhere rather than
  // to a half-built path.
  const id = (key: string) =>
    params[key] ? encodeURIComponent(params[key]) : undefined;
  // A product page, only for someone who can open it.
  if (isProductPage(name)) {
    return open.pages?.has(name)
      ? `${org}${PRODUCT_PAGE_ROUTES[name].path}`
      : undefined;
  }
  switch (name) {
    // Registered with the Engage routes in App.tsx.
    case WORK_PERSONALITY_SURVEY:
      return `${org}/personality-assessment`;
    // The Hire routes App.tsx hands to TrackyrApp (trackyr-ui's AppRouter).
    case LISTING: {
      const listingId = id("listingId");
      return listingId && `${org}/listing/${listingId}`;
    }
    case APPLICANT: {
      const listingId = id("listingId");
      const applicantId = id("applicantId");
      return listingId && applicantId
        ? `${org}/listing/${listingId}/applicants/${applicantId}`
        : undefined;
    }
    // Trackyr's ApplicationRedirect opens it on the applicant.
    case APPLICATION: {
      const applicationId = id("applicationId");
      return applicationId && `${org}/applications/${applicationId}`;
    }
    case REQUISITION: {
      if (!open.requisitions) return undefined;
      const requisitionId = id("requisitionId");
      return requisitionId && `${org}/requisitions/${requisitionId}`;
    }
    default:
      return undefined;
  }
};
